/**
 * Precedent for the Value Estimator — evidence source 1 (PRD §5).
 *
 * "Similar items" in v1 is deterministic: same category and altitude first,
 * then title and description similarity (Jaccard over word sets, the same
 * measure semanticCollisions.ts already uses). No embeddings, no vector index.
 * Every candidate is an item in THIS workspace with an accepted value for the
 * attribute on the same scale — the workspace is the boundary (owner ruling).
 */

import { getSupabaseClient } from './supabaseClient';
import { valueLabel, type PrecedentCandidate } from '../../supabase/functions/_shared/estimateValueCore.ts';

// Ranking is the behavior's shared core (one copy with the server runtime).
export { scorePrecedent, rankPrecedents, type PrecedentCandidate, type PrecedentTarget } from '../../supabase/functions/_shared/estimateValueCore.ts';
/**
 * Load every item in the workspace that has an accepted value for the
 * attribute on the given scale (null scale = the attribute's single scale).
 * Two reads: accepted values, then the items they belong to. `accepted_values`
 * carries workspace_id, so the boundary is enforced in the first read.
 */
export async function loadPrecedentCandidates(params: {
  workspaceId: string;
  attributeKey: string;
  scaleId: string | null;
  excludeItemId?: string;
  limit?: number;
}): Promise<{ candidates: PrecedentCandidate[]; error: string | null }> {
  const supabase = getSupabaseClient();
  let valuesQuery = supabase
    .from('accepted_values')
    .select('item_id, value, scale_id, accepted_at')
    .eq('workspace_id', params.workspaceId)
    .eq('attribute_key', params.attributeKey)
    .is('session_scope', null)
    .order('accepted_at', { ascending: false })
    .limit(params.limit ?? 400);
  valuesQuery = params.scaleId ? valuesQuery.eq('scale_id', params.scaleId) : valuesQuery.is('scale_id', null);
  const { data: values, error: valuesError } = await valuesQuery;
  if (valuesError) return { candidates: [], error: valuesError.message };

  const byItem = new Map<string, string>();
  for (const row of (values ?? []) as Array<{ item_id: string; value: unknown }>) {
    if (row.item_id === params.excludeItemId) continue;
    if (!byItem.has(row.item_id)) byItem.set(row.item_id, valueLabel(row.value));
  }
  const ids = [...byItem.keys()];
  if (!ids.length) return { candidates: [], error: null };

  const { data: items, error: itemsError } = await supabase
    .from('items')
    .select('id, title, description, item_category, altitude_level, exit_status')
    .in('id', ids);
  if (itemsError) return { candidates: [], error: itemsError.message };

  const candidates: PrecedentCandidate[] = ((items ?? []) as Array<Record<string, unknown>>)
    .filter((row) => row.exit_status !== 'archived')
    .map((row) => ({
      itemId: row.id as string,
      title: (row.title as string) ?? '',
      description: (row.description as string | null) ?? null,
      category: (row.item_category as string | null) ?? null,
      altitudeLevel: (row.altitude_level as number | null) ?? null,
      value: byItem.get(row.id as string) ?? '',
    }))
    .filter((c) => c.value !== '');
  return { candidates, error: null };
}
